import React, { useState, useCallback, useEffect } from "react";
import {
  MinusSquareOutlined,
  LeftSquareOutlined,
  RightSquareOutlined,
} from "@ant-design/icons";
import { DndContext, useDraggable } from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { Resizable } from "re-resizable";

import { ReactComponent as ResizeIcon } from "@/assets/icons/resizeDownRight2.svg";
import styles from "./DraggableWindow.module.scss";

// Shared counter so clicking a window brings it above all other DraggableWindows
let topZIndex = 1000;
const nextTopZIndex = () => (topZIndex += 1);

/**
 * DraggableContent - Inner draggable content component
 */
const DraggableContent = ({
  children,
  position,
  isCollapsed,
  onMinimize,
  queueLength,
  currentIndex,
  onPrevious,
  onNext,
  title,
  zIndex,
  onBringToFront,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: "window",
  });

  const hasQueue = queueLength > 0;

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    left: position.x,
    top: position.y,
    zIndex,
  };

  return (
    <div
      ref={setNodeRef}
      className={`${styles.floatingWindow} ${
        isCollapsed ? styles.collapsed : ""
      }`}
      style={style}
      onPointerDown={onBringToFront}
    >
      <Resizable
        defaultSize={{
          width: 450,
          height: 650,
        }}
        minWidth={320}
        minHeight={400}
        maxHeight={"90vh"}
        maxWidth={"1200px"}
        className={styles.resizableContainer}
        handleStyles={{
          bottomRight: { right: "0px", bottom: "0px" },
        }}
        handleComponent={{
          bottomRight: (
            <div className={styles.resizeIcon}>
              <ResizeIcon height={20} width={20} />
            </div>
          ),
        }}
        enable={{
          top: false,
          right: false,
          bottom: false,
          left: false,
          topRight: false,
          bottomRight: true,
          bottomLeft: false,
          topLeft: false,
        }}
      >
        <div className={styles.windowHeader} {...listeners} {...attributes}>
          <div className={styles.dragHandle}>
            <span className={styles.windowHeaderTitle}>
              {title || "Communications"}
            </span>
            <div className={styles.headerActions}>
              {hasQueue && (
                <div className={styles.queueNav}>
                  <LeftSquareOutlined
                    className={`${styles.navIcon} ${
                      currentIndex === 0 ? styles.disabled : ""
                    }`}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (currentIndex > 0) onPrevious?.();
                    }}
                  />
                  <span className={styles.queuePosition}>
                    {currentIndex + 1}/{queueLength}
                  </span>
                  <RightSquareOutlined
                    className={`${styles.navIcon} ${
                      currentIndex >= queueLength - 1 ? styles.disabled : ""
                    }`}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (currentIndex < queueLength - 1) onNext?.();
                    }}
                  />
                </div>
              )}
              <MinusSquareOutlined
                className={styles.minimizeIcon}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onMinimize?.();
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles.windowContent}>{children}</div>
      </Resizable>
    </div>
  );
};

/**
 * DraggableWindow - A draggable and resizable floating window component
 */
const DraggableWindow = ({
  children,
  defaultPosition,
  isCollapsed,
  onMinimize,
  queueLength,
  currentIndex,
  onPrevious,
  onNext,
  title,
}) => {
  const resetPosition = defaultPosition || {
    x: window.innerWidth - 475,
    y: 75,
  };
  const [position, setPosition] = useState(resetPosition);
  const [zIndex, setZIndex] = useState(() => nextTopZIndex());

  const bringToFront = useCallback(() => {
    setZIndex((current) => (current === topZIndex ? current : nextTopZIndex()));
  }, []);

  // Reset position when window is expanded, and bring to front.
  useEffect(() => {
    if (!isCollapsed) {
      setPosition(resetPosition);
      setZIndex(nextTopZIndex());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsed]);

  const handleDragEnd = useCallback((event) => {
    const { delta } = event;
    setPosition((prev) => ({
      x: Math.max(0, prev.x + delta.x),
      y: Math.max(0, prev.y + delta.y),
    }));
  }, []);

  return (
    <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToWindowEdges]}>
      <DraggableContent
        position={position}
        isCollapsed={isCollapsed}
        onMinimize={onMinimize}
        queueLength={queueLength}
        currentIndex={currentIndex}
        onPrevious={onPrevious}
        onNext={onNext}
        title={title}
        zIndex={zIndex}
        onBringToFront={bringToFront}
      >
        {children}
      </DraggableContent>
    </DndContext>
  );
};

export default DraggableWindow;
